import React, { useState } from 'react';
import { Clock, CheckCircle, XCircle, User, Building, Loader, GitPullRequestArrow, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

export const CheckoutRequestsList = ({
  requests,
  pagination,
  onApprove,
  onReject,
  loading = false,
  currentPage,
  setCurrentPage
}) => {
  const { t } = useTranslation();
  const itemsPerPage = pagination.itemsPerPage; // Adjust as needed

  const totalPages = pagination.totalPages;
  const paginatedRequests = requests;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock size={16} className="text-orange-600" />;
      case 'approved':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'rejected':
        return <XCircle size={16} className="text-red-600" />;
      default:
        return null;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'early':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'late':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'onTime':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 text-center">
        <Loader size={48} className="mx-auto text-blue-600 mb-4 animate-spin" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">{t('loading')}</h3>
        <p className="text-slate-500">
          {t('loadingCheckoutRequests')}
        </p>
      </div>
    );
  }

  if (!requests || requests.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 text-center">
        <Clock size={48} className="mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">{t('noRequests')}</h3>
        <p className="text-slate-500">
          {t('noRequestsDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900">
          {t('checkoutRequests')} ({pagination.totalItems})
        </h3>
      </div>

      <div className="divide-y divide-slate-200">
        {paginatedRequests.map((request) => (
          <div key={request._id} className="p-6 hover:bg-slate-50 transition-colors duration-200">
            <div className="flex flex-wrap items-start justify-between">
              <div className="">
                <div className="flex flex-wrap items-center space-y-2 space-x-3 rtl:space-x-reverse mb-3">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <User size={18} className="text-slate-600" />
                    <span className="font-semibold text-slate-900">{request.checkoutReqClientName || t('unknown')}</span>
                  </div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <Building size={18} className="text-slate-600" />
                    <span className="text-slate-700">{request.unit?.unitName || t('unknown')} - {request.unit?.unitCode || t('unknown')}</span>
                  </div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <GitPullRequestArrow size={18} className="text-slate-600" />
                    <span className="text-slate-700">{request.request?.reqCode || t('unknown')}</span>
                  </div>
                  <span className={`inline-flex items-center space-x-1 rtl:space-x-reverse px-2 py-1 rounded-full text-xs font-medium border ${getTypeColor(request.checkoutType)}`}>
                    {request.checkoutType === 'early' ? t('earlyCheckout') :
                      request.checkoutType === 'late' ? t('lateCheckout') :
                        request.checkoutType === 'onTime' ? t('onTimeCheckout') : t('unknown')}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-5 text-sm mb-4">
                  <div>
                    <span className="text-slate-600">{t('originalCheckout')}:</span>
                    <div className="font-medium text-slate-900">
                      {request.originalCheckOut ? moment.utc(request.originalCheckOut).format('L LT') : t('unknown')}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-600">{t('requestedCheckout')}:</span>
                    <div className="font-medium text-slate-900">
                      {moment.utc(request.requestedCheckOut).format('L LT')}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-600">{t('status')}:</span>
                    <div className={`flex items-center space-x-1 rtl:space-x-reverse font-medium ${request.status === 'approved'
                      ? 'text-green-600'
                      : request.status === 'rejected'
                        ? 'text-red-600'
                        : 'text-orange-600'
                      }`}>
                      {getStatusIcon(request.status)}
                      <span>
                        {request.status === 'approved' ? t('approved') :
                          request.status === 'rejected' ? t('rejected') : t('pending')}
                      </span>
                    </div>
                  </div>
                  {request.finalAmount !== undefined && request.finalAmount !== 0 && (
                    <div>
                      <span className="text-slate-600">
                        {request.finalAmount < 0 ? t('refund') : t('additionalFee')}:
                      </span>
                      <div className={`font-bold ${request.finalAmount < 0 ? 'text-green-600' : 'text-orange-600'
                        }`}>
                        {request.checkoutType == "early" ? '-' : '+'}{Math.abs(request.finalAmount).toLocaleString()} {t('EGP')}
                      </div>
                    </div>
                  )}
                </div>

                {request.reason && (
                  <div className="mb-4">
                    <span className="text-slate-600 text-sm">{t('reason')}:</span>
                    <p className="text-slate-700 text-sm mt-1">{request.reason}</p>
                  </div>
                )}

                {request.rejectionReason && (
                  <div className="mb-4">
                    <span className="text-red-600 text-sm">{t('rejectionReason')}:</span>
                    <p className="text-red-700 text-sm mt-1">{request.rejectionReason}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              {request.status === 'pending' && (
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <button
                    onClick={() => onApprove(request._id)}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 rtl:space-x-reverse"
                  >
                    {loading ? <Loader size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    <span>{t('approve')}</span>
                  </button>
                  <button
                    onClick={() => onReject(request._id)}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 rtl:space-x-reverse"
                  >
                    {loading ? <Loader size={16} className="animate-spin" /> : <XCircle size={16} />}
                    <span>{t('reject')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center px-6 py-4 border-t border-slate-200">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 rtl:space-x-reverse"
          >
            <ChevronRight size={16} />

            <span>{t('previous')}</span>
          </button>
          <span className="text-sm text-slate-600">
            {t('page')} {currentPage} {t('of')} {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 rtl:space-x-reverse"
          >
            <span>{t('next')}</span>
            <ChevronLeft size={16} />
          </button>
        </div>
      )}
    </div>
  );
};